import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { 
    Users, Search, Plus, LogOut, Menu, X, 
    ChevronRight, Mail, Phone, MapPin, Trash2, Archive, RotateCcw, Pencil
} from 'lucide-react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientList = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [clientToDelete, setClientToDelete] = useState(null);
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [clientToChangeStatus, setClientToChangeStatus] = useState(null);
    const [newStatus, setNewStatus] = useState('');
    const [activeTab, setActiveTab] = useState('active_retained');
    const [myClientsOnly, setMyClientsOnly] = useState(false);
    const [counts, setCounts] = useState({ active_retained: 0, active_not_retained: 0, inactive: 0, total: 0 });

    useEffect(() => {
        fetchCounts();
    }, []);

    useEffect(() => {
        fetchClients();
    }, [search, activeTab, myClientsOnly]);

    const fetchCounts = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/clients/stats/counts`);
            setCounts(response.data);
        } catch (error) {
            console.error('Failed to load counts');
        }
    };

    const fetchClients = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/clients`, {
                params: { 
                    search, 
                    status: activeTab,
                    assigned_to: myClientsOnly ? 'me' : ''
                }
            });
            setClients(response.data);
        } catch (error) {
            toast.error('Failed to load clients');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!clientToDelete) return;
        try {
            await axios.delete(`${API_URL}/api/clients/${clientToDelete.id}`);
            toast.success('Client deleted successfully');
            fetchClients();
            fetchCounts();
        } catch (error) {
            toast.error('Failed to delete client');
        } finally {
            setDeleteDialogOpen(false);
            setClientToDelete(null);
        }
    };

    const handleStatusChange = async () => {
        if (!clientToChangeStatus || !newStatus) return;
        try {
            await axios.patch(`${API_URL}/api/clients/${clientToChangeStatus.id}/status`, {
                status: newStatus
            });
            const statusLabels = {
                'inactive': 'marked as inactive',
                'active_retained': 'restored to active',
                'active_not_retained': 'marked as not retained'
            };
            toast.success(`Client ${statusLabels[newStatus] || 'status updated'}`);
            fetchClients();
            fetchCounts();
        } catch (error) {
            toast.error('Failed to update client status');
        } finally {
            setStatusDialogOpen(false);
            setClientToChangeStatus(null);
            setNewStatus('');
        }
    };

    const openStatusDialog = (client, status) => {
        setClientToChangeStatus(client);
        setNewStatus(status);
        setStatusDialogOpen(true);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    // Format middle name/initial - add period if single letter
    const fmtMiddle = (m) => m && m.trim().length === 1 ? m.trim() + '.' : m;
    const fmtFullName = (first, middle, last) => [first, fmtMiddle(middle), last].filter(Boolean).join(' ');

    return (
        <div className="min-h-screen bg-[#FDFCF8]">
            {/* Header */}
            <header className="sticky top-0 z-50 border-b border-stone-200 bg-white/80 backdrop-blur-sm">
                <div className="flex items-center justify-between px-6 py-4 max-w-7xl mx-auto">
                    <Link to="/dashboard" className="flex items-center gap-3">
                        <img src="/lion-logo.png" alt="BestWillsAndTrusts" className="h-10 w-10 object-contain" />
                        <span className="font-serif text-xl font-semibold hidden sm:block text-amber-600">BestWillsAndTrusts</span>
                    </Link>
                    
                    <nav className="hidden md:flex items-center gap-8">
                        <Link to="/dashboard" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">Dashboard</Link>
                        <Link to="/clients" className="text-sm font-medium text-primary">Clients</Link>
                        <Link to="/documents" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">Documents</Link>
                        <Link to="/templates" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">Templates</Link>
                        {user?.role === 'admin' && <Link to="/admin/users" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">Users</Link>}
                    </nav>

                    <div className="flex items-center gap-4">
                        <span className="text-sm text-muted-foreground hidden sm:block">{user?.name}</span>
                        <Button variant="ghost" size="sm" onClick={logout} className="rounded-none">
                            <LogOut className="h-4 w-4 mr-2" />
                            <span className="hidden sm:inline">Sign Out</span>
                        </Button>
                        <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                        </Button>
                    </div>
                </div>
                
                {mobileMenuOpen && (
                    <motion.nav 
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="md:hidden border-t border-stone-200 bg-white px-6 py-4 space-y-4"
                    >
                        <Link to="/dashboard" className="block text-sm font-medium text-muted-foreground">Dashboard</Link>
                        <Link to="/clients" className="block text-sm font-medium text-primary">Clients</Link>
                        <Link to="/documents" className="block text-sm font-medium text-muted-foreground">Documents</Link>
                    </motion.nav>
                )}
            </header>

            {/* Main content */}
            <main className="max-w-7xl mx-auto px-6 py-8">
                {/* Page header */}
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8"
                >
                    <div>
                        <h1 className="font-serif text-3xl lg:text-4xl mb-2">Clients</h1>
                        <p className="text-muted-foreground">
                            Manage your client intake records
                        </p>
                    </div>
                    <Link to="/clients/new">
                        <Button className="rounded-none font-medium uppercase tracking-wide" data-testid="add-client-btn">
                            <Plus className="h-4 w-4 mr-2" />
                            New Client
                        </Button>
                    </Link>
                </motion.div>

                {/* Search and Filter */}
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="mb-6"
                >
                    <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                        <div className="relative max-w-md flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search by name or email..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-10 rounded-none"
                                data-testid="client-search-input"
                            />
                        </div>
                        <Button
                            variant={myClientsOnly ? "default" : "outline"}
                            size="sm"
                            onClick={() => setMyClientsOnly(!myClientsOnly)}
                            className={`rounded-none ${myClientsOnly ? 'bg-amber-500 hover:bg-amber-600' : ''}`}
                            data-testid="my-clients-toggle"
                        >
                            <Users className="h-4 w-4 mr-2" />
                            {myClientsOnly ? 'My Clients' : 'All Clients'}
                        </Button>
                    </div>
                </motion.div>

                {/* Status Tabs */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    className="mb-6"
                >
                    <div className="flex flex-wrap gap-2 border-b border-stone-200">
                        <button
                            onClick={() => setActiveTab('active_retained')}
                            className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                                activeTab === 'active_retained'
                                    ? 'border-amber-500 text-amber-700 bg-amber-50'
                                    : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-stone-50'
                            }`}
                            data-testid="tab-active-retained"
                        >
                            Active & Retained
                            <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">
                                {counts.active_retained}
                            </span>
                        </button>
                        <button
                            onClick={() => setActiveTab('active_not_retained')}
                            className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                                activeTab === 'active_not_retained'
                                    ? 'border-blue-500 text-blue-700 bg-blue-50'
                                    : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-stone-50'
                            }`}
                            data-testid="tab-active-not-retained"
                        >
                            Active & Not Retained
                            <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">
                                {counts.active_not_retained}
                            </span>
                        </button>
                        <button
                            onClick={() => setActiveTab('inactive')}
                            className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                                activeTab === 'inactive'
                                    ? 'border-stone-500 text-stone-700 bg-stone-100'
                                    : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-stone-50'
                            }`}
                            data-testid="tab-inactive"
                        >
                            Inactive
                            <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-stone-200 text-stone-600">
                                {counts.inactive}
                            </span>
                        </button>
                    </div>
                </motion.div>

                {/* Client list */}
                {loading ? (
                    <div className="space-y-4">
                        {[...Array(5)].map((_, i) => (
                            <div key={i} className="h-24 bg-stone-100 animate-pulse rounded-none" />
                        ))}
                    </div>
                ) : clients.length > 0 ? (
                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        className="space-y-4"
                    >
                        {clients.map((client, index) => (
                            <motion.div
                                key={client.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 * index }}
                            >
                                <Card className="rounded-none border-stone-200 hover:border-primary/30 transition-colors card-hover">
                                    <CardContent className="p-0">
                                        <div className="flex items-center justify-between">
                                            <Link 
                                                to={`/clients/${client.id}`}
                                                className="flex-1 p-6"
                                                data-testid={`client-row-${client.id}`}
                                            >
                                                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                                                    <div className="flex-1">
                                                        <h3 className="font-serif text-lg font-medium mb-1">
                                                            {fmtFullName(client.first_name, client.middle_name, client.last_name)}
                                                        </h3>
                                                        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                                                            {client.email && (
                                                                <span className="flex items-center gap-1">
                                                                    <Mail className="h-3.5 w-3.5" />
                                                                    {client.email}
                                                                </span>
                                                            )}
                                                            {client.phone && (
                                                                <span className="flex items-center gap-1">
                                                                    <Phone className="h-3.5 w-3.5" />
                                                                    {client.phone}
                                                                </span>
                                                            )}
                                                            {client.city && client.state && (
                                                                <span className="flex items-center gap-1">
                                                                    <MapPin className="h-3.5 w-3.5" />
                                                                    {client.city}, {client.state}
                                                                </span>
                                                            )}
                                                            {client.assigned_to_name && (
                                                                <span className="flex items-center gap-1 text-amber-600">
                                                                    <Users className="h-3.5 w-3.5" />
                                                                    {client.assigned_to_name}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        <span className="text-sm text-muted-foreground">
                                                            {formatDate(client.created_at)}
                                                        </span>
                                                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                                                    </div>
                                                </div>
                                            </Link>
                                            <div className="pr-4 flex items-center gap-1">
                                                {activeTab !== 'inactive' ? (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            openStatusDialog(client, 'inactive');
                                                        }}
                                                        className="text-muted-foreground hover:text-amber-600"
                                                        title="Mark as Inactive"
                                                        data-testid={`archive-client-${client.id}`}
                                                    >
                                                        <Archive className="h-4 w-4" />
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            openStatusDialog(client, 'active_retained');
                                                        }}
                                                        className="text-muted-foreground hover:text-green-600"
                                                        title="Restore to Active"
                                                        data-testid={`restore-client-${client.id}`}
                                                    >
                                                        <RotateCcw className="h-4 w-4" />
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        navigate(`/clients/${client.id}/edit`);
                                                    }}
                                                    className="text-muted-foreground hover:text-amber-600"
                                                    data-testid={`edit-client-${client.id}`}
                                                >
                                                    <Pencil className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setClientToDelete(client);
                                                        setDeleteDialogOpen(true);
                                                    }}
                                                    className="text-muted-foreground hover:text-destructive"
                                                    data-testid={`delete-client-${client.id}`}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </motion.div>
                ) : (
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-center py-16"
                    >
                        <Users className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
                        <h2 className="font-serif text-2xl mb-2">No clients found</h2>
                        <p className="text-muted-foreground mb-6">
                            {search 
                                ? 'Try a different search term' 
                                : activeTab === 'active_retained' 
                                    ? 'No active retained clients yet'
                                    : activeTab === 'active_not_retained'
                                        ? 'No active clients without retention'
                                        : 'No inactive clients'}
                        </p>
                        {!search && activeTab === 'active_retained' && (
                            <Link to="/clients/new">
                                <Button className="rounded-none">
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add First Client
                                </Button>
                            </Link>
                        )}
                    </motion.div>
                )}
            </main>

            {/* Delete confirmation dialog */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent className="rounded-none">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-serif">Delete Client</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete {clientToDelete?.first_name} {clientToDelete?.last_name}? 
                            This will also delete all associated documents. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={handleDelete}
                            className="rounded-none bg-destructive hover:bg-destructive/90"
                            data-testid="confirm-delete-btn"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Status change confirmation dialog */}
            <AlertDialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
                <AlertDialogContent className="rounded-none">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-serif">
                            {newStatus === 'inactive' ? 'Mark Client as Inactive' : 'Restore Client'}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {newStatus === 'inactive' 
                                ? `Are you sure you want to mark ${clientToChangeStatus?.first_name} ${clientToChangeStatus?.last_name} as inactive? They will be moved to the Inactive tab.`
                                : `Are you sure you want to restore ${clientToChangeStatus?.first_name} ${clientToChangeStatus?.last_name} to Active & Retained status?`
                            }
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={handleStatusChange}
                            className={`rounded-none ${newStatus === 'inactive' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-green-600 hover:bg-green-700'}`}
                            data-testid="confirm-status-change-btn"
                        >
                            {newStatus === 'inactive' ? 'Mark Inactive' : 'Restore'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default ClientList;
